import React, { useState, useCallback, useMemo, useEffect } from 'react'
import { connect, useDispatch } from 'react-redux'

import { downloadStringAsFile } from './../../../../Utils/Misc'
import { escapeCsvString } from './../../../../shared/csv'
import {
  getMasterScheduleData,
  getDataForMissionsImport,
  getZipCodesByChunks,
} from './gql'
import {
  header,
  sortOn,
  getDynamicHeaders,
  exportHeaders,
} from './tableSettings'
import {
  getMasterScheduleMissionData,
  getMasterScheduleMissionExportData,
  formatData,
  formatDataExport,
  getFilters,
  formatDataToFetchPostcodes,
  convertZipCodesToHashMap,
  getStatusesExportedMissions,
  getSortableHeaders,
} from './util'
import { normalizeGraphQLConnection } from '../../../../shared/graphql/utils'
import {
  SORT_OPTIONS,
  getStartEndDateFromWeekFilterArray,
} from '../../../../Utils/Table'
import { MISSION_STATUS_ENUM } from '../../../../shared/constants'
import {
  getCampaignByChunks,
  getCountry,
  importMissionsMasterSchedule,
} from '../../../../Module/graphQL'
import { getMasterScheduleAndGlobalPlanningFiltersPresets } from '../../../../Utils/GlobalSearch/GlobalSearchUtils'
import { getCurrentTimeOfDay } from '../../../../Utils/Date'
import { gqlCampaign, gqlCityPlans, gqlMissions } from './gql'
import { authorization } from '../../../../Module/Auth/Auth'
import { showReduxModal } from '../../../../Reducers/UI/actions'
import { splitArrayIntoChunks } from '../../../../shared/libraries/utils/array'
import useGraphQLFetch from '../../../../Utils/Hooks/useGraphQLFetch'
import moment from 'moment-timezone'

const Controller = ({
  children,
  campaign,
  getCampaignResults,
  ...otherProps
}) => {
  const [data, setData] = useState({
    missions: [],
    cityPlans: [],
    brandAmbassadors: [],
  })
  const [refreshRequestTrigger, setRefreshRequestTrigger] = useState()
  const [isExportLoading, setIsExportLoading] = useState(false)
  const [isImportLoading, setIsImportLoading] = useState(false)

  const dispatch = useDispatch()

  const { name: campaignName } = campaign
  useEffect(() => {
    if (!data.missions.length || !data.missions[0].cityPlan.city) return

    const runNoShowProcess = async () => {
      const city = data.missions[0].cityPlan.city
      const timezone = city.timezone || 'UTC'
      const currentTime = moment().tz(timezone)

      if (currentTime.format('HH:mm:ss') === '00:00:00') {
        await getData({ noShow: true })
      }
    }

    runNoShowProcess()
  }, [data.missions])

  const isClient = authorization.isClient()

  const getData = async ({
    filter,
    first,
    orderBy,
    skip = 0,
    noShow = false,
    ...filters
  }) => {
    const { startDates, endDates } = getStartEndDateFromWeekFilterArray(
      filters.Week,
    )
    const { data } = await getMasterScheduleData({
      campaignId: campaign.id,
      first,
      after: Buffer.from(String(skip)).toString('base64'),
      orderBy: SORT_OPTIONS[filters.orderBy || orderBy],
      locationIds: filters['Location'],
      timeOfDays: filters['Time of day'],
      statusAndAboves: filters['Status'],
      statusAndBelows: filters['Status'],
      dayOfTheWeeks: filters['Day of week'],
      cityIds: filters['City'],
      filter,
      startDates,
      endDates,
      noShow,
    })

    const formattedData = formatData(
      data,
      campaign,
      getMasterScheduleMissionData,
      isClient,
    )

    setData(data => ({ ...data, ...formattedData }))

    return formattedData
  }

  const { data: countryResponse } = useGraphQLFetch({
    fetchFn: getCountry,
    defaultFetchArgs: campaign.country.id,
    fetchId: 'getCountry',
    mapResponse: result => result.country,
  })
  // Export all the data as a file
  const onExportData = async ({ isExportAllMissions = false }) => {
    setIsExportLoading(true)

    const missionStatuses = getStatusesExportedMissions({
      isClient,
      isExportAllMissions,
    })

    const result = await getCampaignByChunks({
      campaignId: campaign.id,
      cityPlanChunk: 1,
      missionChunk: 100,
      missionStatuses,
      queries: { gqlCampaign, gqlCityPlans, gqlMissions },
    })

    const countryWithPostcodes = ['AU', 'BE', 'FR', 'NZ', 'UK', 'US']
    const countryCode = result.cityPlans.nodes[0].city.country.code

    let zipCodesHashMap = {}

    if (countryWithPostcodes.includes(countryCode)) {
      const dataToFetchPostcodes = formatDataToFetchPostcodes(result)

      const zipCodesChunks = splitArrayIntoChunks({
        inputArray: dataToFetchPostcodes.data,
        itemsPerChunk: 100,
      })

      const zipCodes = await getZipCodesByChunks({
        chunks: zipCodesChunks,
        countryCode: dataToFetchPostcodes.countryCode,
      })
      zipCodesHashMap = convertZipCodesToHashMap(zipCodes)
    }

    const { missions } = formatDataExport(
      result.cityPlans.nodes,
      campaign,
      getMasterScheduleMissionExportData,
      zipCodesHashMap,
    )

    const exportMode = true
    const exportHeadersGenerated = getDynamicHeaders(
      exportHeaders,
      campaign.metrics.nodes,
      exportMode,
    )

    var content = exportHeadersGenerated.join(',')
    missions.forEach(m => {
      content +=
        '\n' +
        exportHeadersGenerated
          .map(k => {
            var val = m[k]
            return typeof val === 'string' ? escapeCsvString(val) : val
          })
          .join(',')
    })

    const fileName = `${campaignName}-${
      isExportAllMissions ? 'all-missions' : ' checked-out-missions'
    }.csv`

    downloadStringAsFile(content, fileName, 'text/csv')
    setIsExportLoading(false)
  }

  const filters = getFilters({
    cities: data.cityPlans.map(({ city }) => city),
    locations: data.cityPlans.reduce(
      (acc, { city }) => [
        ...acc,
        ...normalizeGraphQLConnection(city.locations),
      ],
      [],
    ),
  })
  // Bit crap but don't want to break filters if they are shared somewhere else.
  filters[filters.findIndex(f => f.label == 'Status')] = {
    label: 'Status',
    options: [
      { label: 'Draft', value: MISSION_STATUS_ENUM.DRAFT },
      { label: 'Available', value: MISSION_STATUS_ENUM.AVAILABLE },
      { label: 'Booked', value: MISSION_STATUS_ENUM.BOOKED },
      { label: 'Under Review', value: MISSION_STATUS_ENUM.UNDER_REVIEW },
      { label: 'Completed', value: MISSION_STATUS_ENUM.COMPLETED },
      { label: 'Paid', value: MISSION_STATUS_ENUM.PAID },
      { label: 'Cancelled', value: MISSION_STATUS_ENUM.CANCELLED },
    ],
  }

  const getDataForMissionsImportHandler = async () => {
    setIsImportLoading(true)
    const cityPlansIds = data.cityPlans.map(node => node.id)

    let brandAmbassadors = []

    for (const cityPlanId of cityPlansIds) {
      const { data } = await getDataForMissionsImport({
        campaignId: campaign.id,
        cityPlanId,
      })

      const newAmbassadors = data.campaign.cityPlans.nodes.reduce(
        (acc, cityPlan) => {
          const {
            city: { brandAmbassadors },
          } = cityPlan
          acc = [...acc, ...normalizeGraphQLConnection(brandAmbassadors)]
          return acc
        },
        [],
      )

      brandAmbassadors = [...brandAmbassadors, ...newAmbassadors]
    }

    setData(data => ({ ...data, brandAmbassadors }))
    setIsImportLoading(false)
  }

  const onMissionsImportHandler = useCallback(
    async (missions, onProgress, campaignId) => {
      const { isFailed } = await importMissionsMasterSchedule({
        missions,
        onProgress,
        campaignId,
      })

      if (isFailed) {
        return dispatch(
          showReduxModal({
            title: 'Import failed',
            children: (
              <div>
                <div>
                  Errors on import mission. Part of the missions have failed to
                  be uploaded. Please remove successfully uploaded missions from
                  the file and try again.
                </div>
                <br />
                <div>
                  Tip: missions are being uploaded in the order as they placed
                  in the file
                </div>
              </div>
            ),
            hideButtons: false,
            confirmLabel: `OK`,
            hideCancel: true,
            onConfirm: () => {
              setRefreshRequestTrigger(new Date())
            },
          }),
        )
      } else {
        setRefreshRequestTrigger(new Date())
      }
    },
  )

  const city = data.missions[0] ? data.missions[0].cityPlan.city : {}
  const { afternoonCheckInTime, lunchCheckInTime, timezone } = city
  const currentTimeOfDay = getCurrentTimeOfDay({
    afternoonCheckInTime,
    lunchCheckInTime,
    timezone,
  })
  const role = authorization.getUser().role

  const presets = useMemo(
    () => [
      {
        title: 'Filter Presets',
        items: getMasterScheduleAndGlobalPlanningFiltersPresets({
          currentTimeOfDay,
          countryResponse,
          userRole: role,
        }),
      },
    ],
    [countryResponse, currentTimeOfDay],
  )

  const getMasterScheduleFooterData = ({ header }) => {
    const flyerDistributed = data.missions.reduce((acc, mission) => {
      var distributed = Number(mission.totalFlyersDistributed)
      return acc + (isNaN(distributed) ? 0 : distributed)
    }, 0)
    const scans = data.missions.reduce((acc, mission) => {
      var distributed = Number(mission.amountScans)
      return acc + (isNaN(distributed) ? 0 : distributed)
    }, 0)

    const metrics = data.missions.reduce((acc, mission) => {
      const metricResults = mission.metricResults.nodes.map(metric => ({
        ...metric,
        name: metric.metric.label ? metric.metric.label : metric.metric.name,
      }))
      if (metricResults.length == 0) return null
      metricResults.forEach(item => {
        if (item && item.name) {
          const name = item.name == 'Scans' ? 'Scans 2' : item.name
          acc[name] = (acc[name] || 0) + item.value
        }
      })
      return acc
    }, {})

    const metricNames = metrics ? Object.keys(metrics) : null
    let footerData = []

    header.forEach(item => {
      if (item === 'City') {
        footerData.push('Total:')
      } else if (item === 'Flyers distributed') {
        footerData.push(`${flyerDistributed}`)
      } else if (item === 'Scans') {
        footerData.push(`${scans}`)
      } else if (metrics && metricNames.includes(item)) {
        footerData.push(metrics[item])
      } else footerData.push('-')
    })

    return [footerData]
  }

  return React.cloneElement(children, {
    ...otherProps,
    campaign,
    cityPlans: data.cityPlans,
    missions: data.missions,
    brandAmbassadors: data.brandAmbassadors,
    header: getDynamicHeaders(
      header,
      campaign.metrics.nodes,
      false,
      campaign.scanDataEnabled,
    ),
    sortOn: getSortableHeaders({
      countryCode: campaign.country.code,
      campaignType: campaign.type,
      headers: sortOn,
    }),
    filterOn: filters,
    footerRender: getMasterScheduleFooterData,
    onMissionsImport: onMissionsImportHandler,
    refreshRequestTrigger,
    onExportData,
    action: data => window.open(`/dashboard/mission/${data.id}`, '_blank'),
    fetchData: getData,
    getDataForMissionsImport: getDataForMissionsImportHandler,
    presets,
    isExportLoading,
    isImportLoading,
    baRedirectActions: data.baRedirectActions || null,
  })
}

const mapStateToProps = (state, props) => ({
  campaign: state.campaigns.campaigns[props.campaignId],
})

export default connect(mapStateToProps)(Controller)
